import { toast } from "sonner";

/**
 * Centralised wrapper around `authFetch` for write operations (POST/PUT/DELETE)
 * that:
 *  - checks `res.ok` (fetch's `.catch` only fires on network errors, not 4xx/5xx)
 *  - parses the JSON body once and surfaces the server `detail` / `error` text
 *  - shows a single toast on success / failure
 *  - never throws — returns { ok, data, status, error } so callers can chain UI
 *    updates (optimistic removal, redirects, etc.)
 *
 * Usage:
 *   const r = await mutate(authFetch, `${API}/parties/${id}/deactivate`, { method: "POST" }, {
 *     successMessage: "Dealer deactivated",
 *     onSuccess: () => setItems(prev => prev.filter(p => p.id !== id)),
 *   });
 *   if (!r.ok) return;  // error toast already shown
 */
export async function mutate(authFetch, url, init = {}, opts = {}) {
  const {
    successMessage = "",
    failureMessage = "",
    onSuccess,
    silentSuccess = false,
  } = opts;
  let res;
  try {
    res = await authFetch(url, init);
  } catch (err) {
    toast.error(`${failureMessage || "Network error"}: ${err?.message || "unreachable"}`);
    return { ok: false, status: 0, error: err?.message || "network" };
  }

  let body = null;
  try { body = await res.json(); } catch { /* non-JSON body */ }

  if (!res.ok) {
    const detail =
      body?.detail || body?.error || body?.message ||
      (failureMessage || `Failed (${res.status})`);
    toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    return { ok: false, status: res.status, data: body, error: detail };
  }

  // Backend may wrap as {success:true, data:...} OR return the resource directly.
  if (body && body.success === false) {
    const detail = body.detail || body.error || body.message || "Operation failed";
    toast.error(detail);
    return { ok: false, status: res.status, data: body, error: detail };
  }

  if (!silentSuccess && successMessage) toast.success(successMessage);
  if (onSuccess) onSuccess(body);
  return { ok: true, status: res.status, data: body };
}

/**
 * Convenience wrapper for DELETE with a confirm() prompt.
 */
export async function confirmDelete(authFetch, url, opts = {}) {
  const { promptMessage = "Are you sure?" } = opts;
  if (!window.confirm(promptMessage)) return { ok: false, cancelled: true };
  return mutate(authFetch, url, { method: "DELETE" }, opts);
}

export default mutate;
